/**
 * TaxModal — SW-8538 Phase B.
 *
 * The modal now ONLY renders Step 2 (VAT Information). Step 1 (plan picker)
 * has been removed; callers that need a plan picker push `/app/plans` and
 * land on the Pricing page instead.
 *
 * Open state is driven by the Zustand `vatCheckoutStore`. A caller invokes
 * `openVatModal({ onComplete, planContext })`; the modal mounts Step 2 with
 * `props.onComplete` plumbed through to `VatCheckoutStep`. On successful
 * `/api/vat-customer-update`, `VatCheckoutStep` calls `onComplete()` —
 * Pricing runs `subscription.changePlan(...)`, SubscribeButton runs the
 * legacy Stripe `createSession`, etc.
 *
 * This decouples the modal from the legacy `?upgrade=2` URL pattern. The
 * URL no longer mounts the modal; the store does.
 */
import { useEffect } from "react";
import mixpanel from "../../../MixpanelConfiguration";
import { sendAppEvent } from "../../../Utils";
import { useVatCheckoutStore } from "../../../stores/vatCheckoutStore";
import useVatCheckoutGate from "../../../hooks/useVatCheckoutGate";
import { VatCheckoutStep } from "../../VatCheckout";
import {
  sendVatStepViewed,
  sendVatStepSkipped,
} from "../../VatCheckout/analytics";
import GeneralBaseModal from "../GeneralBaseModal";
import { Container } from "./styles";

export default function TaxModal() {
  const isOpen = useVatCheckoutStore((state) => state.isOpen);
  const onComplete = useVatCheckoutStore((state) => state.onComplete);
  const planContext = useVatCheckoutStore((state) => state.planContext);
  const origin = useVatCheckoutStore((state) => state.origin);
  const closeVatModal = useVatCheckoutStore((state) => state.closeVatModal);

  // Per the store's docblock, `origin` is the click-source tag passed by the
  // opener (e.g. "poem", "twist", "rightbar", "editor_trial_expired") and is
  // emitted as the `ref` field on Subscribe Modal {Viewed,Dismissed} so the
  // existing Mixpanel funnel can attribute clicks to their entry point. Fall
  // back to "vat_step" when a caller opens the modal without an origin (auto-
  // skip path / future callers) so the field is never null.
  const analyticsRef = origin || "vat_step";

  const {
    showVatStep,
    country: vatCountry,
    classification: vatClassificationKind,
    source: vatClassificationSource,
    isLoading: vatGateLoading,
  } = useVatCheckoutGate();

  // Auto-close + analytics if the gate decides this user shouldn't see the
  // VAT step (non-EU, geo error). Belt-and-suspenders — the modal openers
  // (SubscribeButton, Pricing) already gate on isEU before opening.
  //
  // Geolocation failures surface as `vatCountry === null` (useVatCountry
  // swallows fetch errors by design — see its docblock); we use that as
  // the "geolocation_unavailable" signal.
  useEffect(() => {
    if (!isOpen || vatGateLoading) return;
    if (!showVatStep) {
      sendVatStepSkipped({
        reason: !vatCountry ? "geolocation_unavailable" : "non_eu",
      });
      closeVatModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, vatGateLoading, showVatStep]);

  // Analytics: fire "Step Viewed" once per modal-open lifecycle when Step 2
  // is rendered.
  useEffect(() => {
    if (!isOpen || !showVatStep) return;
    mixpanel.start_session_recording();
    sendAppEvent("Subscribe Modal Viewed", {
      ref: analyticsRef,
      plan_id: planContext?.id,
      plan_interval: planContext?.interval,
    });
    sendVatStepViewed({
      country: vatCountry,
      classification: vatClassificationKind,
      source: vatClassificationSource,
      plan_id: planContext?.id,
      plan_interval: planContext?.interval,
    });
    return () => {
      mixpanel.stop_session_recording();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showVatStep]);

  const handleClose = () => {
    sendAppEvent("Subscribe Modal Dismissed", {
      ref: analyticsRef,
      step: "VAT Information",
      plan_id: planContext?.id,
      plan_interval: planContext?.interval,
    });
    closeVatModal();
  };

  // VatCheckoutStep handles its own POST + error UI; on success it invokes
  // `onComplete`. We close the modal AFTER the caller's continuation kicks
  // off so the user doesn't see a flash of empty page before the redirect.
  const handleComplete = async () => {
    try {
      if (typeof onComplete === "function") {
        await onComplete();
      }
    } finally {
      // Defensive close — the continuation may navigate away (full-page
      // Stripe redirect) but if it's an in-app changePlan we want the modal
      // gone before the page reloads.
      closeVatModal();
    }
  };

  if (!isOpen) return null;

  // Wait for the gate to settle. Until it does we render an empty modal
  // shell so the user doesn't see a flash of "non-EU; closing" before geo
  // resolves.
  if (vatGateLoading) {
    return (
      <GeneralBaseModal
        isOpen={isOpen}
        onClose={handleClose}
        setIsOpen={() => {}}
      >
        <Container />
      </GeneralBaseModal>
    );
  }

  if (!showVatStep) return null;

  return (
    <GeneralBaseModal
      isOpen={isOpen}
      onClose={handleClose}
      setIsOpen={() => {}}
    >
      <Container>
        <VatCheckoutStep
          selectedPlan={planContext}
          planInterval={planContext?.interval}
          onComplete={handleComplete}
          onBack={handleClose}
          loading={false}
          userCountry={vatCountry}
          classification={vatClassificationKind}
          origin={origin}
        />
      </Container>
    </GeneralBaseModal>
  );
}
